import { z } from 'zod';

import {
  createAvailableTimeSchema,
  createDealerSchema,
  createDistrictSchema,
  createEmailSchema,
  createFullNameSchema,
  createLineIdSchema,
  createMobileAsLineOptionalSchema,
  createMobileSchema,
  createPrivacySchema,
  createProvinceSchema,
} from '@/app/lib/forms/schemas/base-fields.schema';
import type { Locale } from '@/app/lib/forms/types/form.types';

/**
 * Base schema with only fullname and mobile
 */
export const createBaseFieldsSchema = (locale: Locale = 'th') => {
  const fullNameSchema = createFullNameSchema(locale);
  const mobileSchema = createMobileSchema(locale);

  return z.object({
    fullname: fullNameSchema,
    mobile: mobileSchema,
  });
};

/**
 * Base schema with only fullname and mobile and privacy
 * This is the minimal schema for all lead forms
 */
export const createBaseLeadFormSchema = (locale: Locale = 'th') => {
  const fullNameSchema = createFullNameSchema(locale);
  const mobileSchema = createMobileSchema(locale);
  const privacySchema = createPrivacySchema(locale);

  return z.object({
    fullname: fullNameSchema,
    mobile: mobileSchema,
    privacy: privacySchema,
  });
};

/**
 * Location schema with province, district, and dealer
 * Used for forms that require location selection
 */
export const createBaseLocationSchema = (locale: Locale = 'th') => {
  const provinceSchema = createProvinceSchema(locale);
  const districtSchema = createDistrictSchema();
  const dealerSchema = createDealerSchema(locale);

  return z.object({
    province: provinceSchema,
    district: districtSchema,
    dealer: dealerSchema,
  });
};

/**
 * Available time and privacy schema
 * Used as the ending fields for most lead forms
 */
export const createAvailableTimeAndPrivacySchema = (locale: Locale = 'th') => {
  const availableTimeSchema = createAvailableTimeSchema(locale);
  const privacySchema = createPrivacySchema(locale);

  return z.object({
    availableTime: availableTimeSchema,
    privacy: privacySchema,
  });
};

/**
 * Full lead form schema
 * Extends base with location, available time, and privacy
 */
export const createLeadFormSchema = (locale: Locale = 'th') => {
  const baseSchema = createBaseFieldsSchema(locale);
  const locationSchema = createBaseLocationSchema(locale);
  const endingSchema = createAvailableTimeAndPrivacySchema(locale);

  return baseSchema.extend({
    ...locationSchema.shape,
    ...endingSchema.shape,
  });
};

/**
 * Lead form with email
 * Extends base with location, email, available time, and privacy
 */
export const createLeadFormWithEmailSchema = (locale: Locale = 'th') => {
  const baseSchema = createBaseFieldsSchema(locale);
  const locationSchema = createBaseLocationSchema(locale);
  const emailSchema = createEmailSchema(locale);
  const endingSchema = createAvailableTimeAndPrivacySchema(locale);

  return baseSchema.extend({
    ...locationSchema.shape,
    email: emailSchema,
    ...endingSchema.shape,
  });
};

/**
 * Lead form with LINE ID
 * Extends base with lineId, mobileAsLine, email, available time, and privacy
 * When mobileAsLine is true, LINE ID is ignored (phone is used as LINE contact).
 */
export const createLeadFormWithLineIdSchema = (locale: Locale = 'th') => {
  const baseSchema = createBaseFieldsSchema(locale);
  const mobileAsLineSchema = createMobileAsLineOptionalSchema();
  const emailSchema = createEmailSchema(locale);
  const endingSchema = createAvailableTimeAndPrivacySchema(locale);

  return baseSchema
    .extend({
      lineId: z.string().nullable().optional(),
      mobileAsLine: mobileAsLineSchema,
      email: emailSchema,
      ...endingSchema.shape,
    })
    .superRefine((data, ctx) => {
      if (data.mobileAsLine) return;

      const result = createLineIdSchema(locale).safeParse(data.lineId);
      if (result.success) return;

      for (const issue of result.error.issues) {
        ctx.addIssue({
          code: 'custom',
          message: issue.message,
          path: ['lineId'],
        });
        break;
      }
    });
};

/**
 * Name and tel form with optional province
 */
export const createLeadFormWithNameAndTelSchema = (locale: Locale = 'th') => {
  const baseSchema = createBaseFieldsSchema(locale);
  const provinceSchema = createProvinceSchema();
  const privacySchema = createPrivacySchema(locale);

  return baseSchema.extend({
    province: provinceSchema.optional(),
    privacy: privacySchema,
  });
};

/**
 * Lead form with province only and have mobileAsLine
 * Extends base with province (no district), mobileAsLine, available time, and privacy
 */
export const createLeadFormWithProvinceSchema = (locale: Locale = 'th') => {
  const baseSchema = createBaseFieldsSchema(locale);
  const provinceSchema = createProvinceSchema(locale).optional();
  const mobileAsLineSchema = createMobileAsLineOptionalSchema();
  const endingSchema = createAvailableTimeAndPrivacySchema(locale);

  return baseSchema.extend({
    province: provinceSchema,
    mobileAsLine: mobileAsLineSchema,
    ...endingSchema.shape,
  });
};

export type BaseLeadForm = z.infer<ReturnType<typeof createBaseFieldsSchema>>;
export type BaseLocation = z.infer<ReturnType<typeof createBaseLocationSchema>>;
export type LeadForm = z.infer<ReturnType<typeof createLeadFormSchema>>;
export type FormWithEmail = z.infer<
  ReturnType<typeof createLeadFormWithEmailSchema>
>;
export type FormWithLineId = z.infer<
  ReturnType<typeof createLeadFormWithLineIdSchema>
>;
export type FormWithNameAndTel = z.infer<
  ReturnType<typeof createLeadFormWithNameAndTelSchema>
>;
export type FormWithProvince = z.infer<
  ReturnType<typeof createLeadFormWithProvinceSchema>
>;

/** All fields LeadForm may read/write at runtime (schema subsets vary by campaign). */
export type LeadFormValues = {
  fullname?: string;
  mobile?: string;
  lineId?: string;
  email?: string;
  province?: string;
  district?: string;
  dealer?: string;
  availableTime?: string;
  mobileAsLine?: boolean;
  privacy?: boolean;
  model?: 'pickup' | 'ppv';
};
